// src/encounter/services/use-save-medication.ts
// IPS Medications CRUD (PROD-1257).
//   create → POST /api/Patients/CreateMedication (body from to-medication-request)
//   delete → soft-delete WITHOUT field loss: fetch the FULL record
//            (GET /api/Patients/GetMedicationDetails?Id=) then PUT it back with
//            isDeleted:true. The rail only has the lossy GetPatientSummary projection, so
//            building the PUT body from the summary would blank the record's other server
//            fields on a full-replace (Codex HIGH, PR review) — round-tripping the full
//            record preserves them. Mirrors Flutter getMedicationDetails + updateMedication.
// On success, invalidate the patient summary so the rail Medications section refreshes.
// dev-api routed: CreateMedication / medication PUT / GetMedicationDetails all reachable (2026-06-19).
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { get, post, put } from '@/shared/api/api-client';
import {
  applyMedicationEdit,
  buildStopMedicationBody,
  type CreateMedicationBody,
  type MedicationFormInput,
} from '../lib/to-medication-request';

/** Full medication record (GET GetMedicationDetails) — kept opaque on purpose so the
 *  soft-delete round-trips every field verbatim without re-modelling the wire shape. */
export type MedicationRecord = Record<string, unknown>;

export function getMedicationDetails(id: string): Promise<MedicationRecord> {
  return get<MedicationRecord>(`/api/Patients/GetMedicationDetails?Id=${encodeURIComponent(id)}`);
}

export type SaveMedicationVars =
  | { kind: 'create'; body: CreateMedicationBody }
  | { kind: 'delete'; identifier: string }
  // PROD-1493 — both ride the same fetch-full → PUT round-trip the delete path proved out.
  | { kind: 'edit'; identifier: string; input: MedicationFormInput }
  | { kind: 'stop'; identifier: string; endYyyymmdd: string; reason?: string };

export function useSaveMedication(patientId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<string, Error, SaveMedicationVars>({
    mutationFn: async (vars) => {
      if (vars.kind === 'create') {
        return post<string>('/api/Patients/CreateMedication', vars.body);
      }
      // edit / stop / delete: fetch the full record, apply the change, round-trip the whole
      // body (no field loss — the PROD-1257 lesson). KNOWN EXPOSURE (accepted, matches every
      // other IPS write + Flutter): the GET→PUT window is last-write-wins — a concurrent edit
      // of the same record between our fetch and PUT is overwritten. The BE offers no
      // ETag/version to guard it; the window is sub-second and single-clinician-per-patient
      // is the operating model.
      const full = await getMedicationDetails(vars.identifier);
      const body =
        vars.kind === 'edit'
          ? applyMedicationEdit(full, vars.input)
          : vars.kind === 'stop'
            ? buildStopMedicationBody(full, { endYyyymmdd: vars.endYyyymmdd, reason: vars.reason })
            : { ...full, isDeleted: true };
      return put<string>(`/api/Patients/medication/${vars.identifier}`, body);
    },
    onSuccess: () => {
      // Match use-patient-summary's queryKey (['patient-summary', patientId]).
      qc.invalidateQueries({ queryKey: ['patient-summary', patientId] });
    },
  });
}
